import { useState, type ReactNode } from 'react'
import { useD1TableData, type D1Filter } from '../hooks/useD1Browser'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Code } from 'lucide-react'
import { cn } from '@/lib/utils'
import { JsonViewer } from '@/components/JsonViewer'

export interface D1FilteredTableProps {
  table: string
  /** Single-column equality shortcut. Combined with `filters` if both supplied. */
  filterCol?: string
  filterVal?: string | number
  /** Multi-column equality (AND-joined). */
  filters?: D1Filter[]
  /** D1 binding name (`'seeds' | 'geo'`). Defaults to seeds. */
  db?: string
  /** Custom render for an expanded row. If omitted, expanding shows a JSON dialog. */
  customRowDetail?: (row: Record<string, unknown>) => ReactNode
  /** Page size. Defaults to 50. */
  limit?: number
  /** Optional small caption above the table. */
  title?: string
  /** Optional empty-state message override. */
  emptyMessage?: string
}

/**
 * Reusable D1 table viewer with pagination, JSON cell drill-down, and
 * server-side single-column equality filtering. Used by D1BrowserPage and
 * the per-share / per-seed inspectors.
 */
export function D1FilteredTable({
  table,
  filterCol,
  filterVal,
  filters,
  db = 'seeds',
  customRowDetail,
  limit = 50,
  title,
  emptyMessage = 'No rows',
}: D1FilteredTableProps) {
  const [offset, setOffset] = useState(0)
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [jsonDialog, setJsonDialog] = useState<{ title: string; data: unknown } | null>(null)

  const { data, loading, error } = useD1TableData(table, {
    limit,
    offset,
    db,
    filterCol,
    filterVal,
    filters,
  })

  if (loading) {
    return (
      <div className="px-4 pb-4 space-y-2">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
      </div>
    )
  }

  if (error) {
    return <div className="px-4 pb-4 text-sm text-destructive">{error}</div>
  }

  if (!data || data.rows.length === 0) {
    return (
      <div className="px-4 pb-4 text-sm text-muted-foreground">
        {title && <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{title}</p>}
        {emptyMessage}
      </div>
    )
  }

  function handleRowAction(row: Record<string, unknown>, i: number) {
    if (customRowDetail) {
      setExpandedRow(expandedRow === i ? null : i)
    } else {
      setJsonDialog({ title: `${table} — Row ${offset + i + 1}`, data: row })
    }
  }

  function handleCellClick(col: string, val: unknown) {
    if (val !== null && val !== undefined && typeof val === 'string' && val.length > 80) {
      try {
        const parsed = JSON.parse(val)
        setJsonDialog({ title: col, data: parsed })
        return
      } catch { /* not JSON, fall through */ }
    }
    if (val !== null && val !== undefined && typeof val === 'object') {
      setJsonDialog({ title: col, data: val })
    }
  }

  return (
    <div className={title ? '' : 'border-t'}>
      <JsonViewer
        open={!!jsonDialog}
        onOpenChange={(open) => { if (!open) setJsonDialog(null) }}
        title={jsonDialog?.title}
        data={jsonDialog?.data}
      />
      {title && (
        <div className="px-4 pt-3 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8 px-3"></TableHead>
              {data.columns.map(col => (
                <TableHead key={col} className="text-xs font-mono whitespace-nowrap">{col}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((row, i) => (
              <>
                <TableRow key={i} className="hover:bg-muted/20">
                  <TableCell className="w-8 px-3">
                    <button
                      onClick={() => handleRowAction(row, i)}
                      className="p-1 rounded hover:bg-muted transition-colors"
                      title="View row detail"
                    >
                      <Code className={cn(
                        'size-3.5',
                        expandedRow === i ? 'text-primary' : 'text-muted-foreground',
                      )} />
                    </button>
                  </TableCell>
                  {data.columns.map(col => {
                    const val = row[col]
                    const isClickable = (val !== null && val !== undefined) &&
                      (typeof val === 'object' || (typeof val === 'string' && val.length > 80))
                    return (
                      <TableCell
                        key={col}
                        className={cn(
                          'text-sm whitespace-nowrap max-w-64 truncate',
                          isClickable && 'cursor-pointer hover:text-primary',
                        )}
                        onClick={isClickable ? () => handleCellClick(col, val) : undefined}
                      >
                        {val === null || val === undefined
                          ? <span className="text-muted-foreground">NULL</span>
                          : typeof val === 'object'
                            ? <span className="font-mono text-xs text-muted-foreground">{JSON.stringify(val).slice(0, 80)}</span>
                            : <span>{String(val).length > 80 ? String(val).slice(0, 80) + '...' : String(val)}</span>
                        }
                      </TableCell>
                    )
                  })}
                </TableRow>
                {customRowDetail && expandedRow === i && (
                  <TableRow key={`${i}-expanded`} className="hover:bg-transparent">
                    <TableCell colSpan={data.columns.length + 1} className="p-0">
                      <div className="bg-muted/30 border-t px-4 py-3">
                        {customRowDetail(row)}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </div>

      {data.totalRows > limit && (
        <div className="px-4 py-2 border-t bg-muted/20 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + limit, data.totalRows)} of {data.totalRows} rows
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              <ChevronLeft className="size-3.5 mr-1" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= data.totalRows}
              onClick={() => setOffset(offset + limit)}
            >
              Next <ChevronRight className="size-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
